import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Users, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", company: "", email: "", phone: "", website: "", industry: "", notes: "",
  status: "active", billing_model: "retainer", billing_amount: 0, billing_start_date: "",
};

export default function Clients() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  // Inline billing editor
  const [editingBilling, setEditingBilling] = useState(null); // client id
  const [billingDraft, setBillingDraft] = useState({ billing_model: "retainer", billing_amount: 0, billing_start_date: "" });

  const load = async () => { const r = await api.get("/clients"); setClients(r.data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/clients", form);
      toast.success("Client added");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startBillingEdit = (c) => {
    setEditingBilling(c.id);
    setBillingDraft({
      billing_model: c.billing_model || "retainer",
      billing_amount: c.billing_amount ?? c.monthly_retainer ?? 0,
      billing_start_date: c.billing_start_date || "",
    });
  };

  const saveBilling = async (c) => {
    const v = parseFloat(billingDraft.billing_amount);
    if (Number.isNaN(v) || v < 0) {
      toast.error("Enter a non-negative amount");
      return;
    }
    try {
      await api.put(`/clients/${c.id}`, {
        ...c,
        billing_model: billingDraft.billing_model,
        billing_amount: v,
        billing_start_date: billingDraft.billing_model === "project_fee" ? (billingDraft.billing_start_date || null) : null,
      });
      toast.success("Billing updated");
      setEditingBilling(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString()}`;
  const billingLabel = (c) => {
    const model = c.billing_model || "retainer";
    const amount = c.billing_amount ?? c.monthly_retainer ?? 0;
    if (!amount) return "—";
    return model === "retainer" ? `${fmtMoney(amount)} / mo` : `${fmtMoney(amount)} fee`;
  };

  return (
    <div data-testid="clients-page">
      <PageHeader
        eyebrow="CRM"
        title="Clients"
        description="Every relationship that pays the bills."
        testid="clients-header"
        actions={
          isAdmin && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="new-client-button">
                  <Plus size={14} className="mr-1" /> Add client
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg rounded-sm">
                <DialogHeader><DialogTitle>Add client</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Company</Label><Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Xebec Communications" data-testid="client-company-input" /></div>
                    <div><Label>Contact name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" data-testid="client-name-input" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="client-email-input" /></div>
                    <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="client-phone-input" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Website</Label><Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} data-testid="client-website-input" /></div>
                    <div><Label>Industry</Label><Input value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} data-testid="client-industry-input" /></div>
                  </div>

                  <div className="border-t border-neutral-200 pt-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-2">Billing</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Billing model</Label>
                        <Select value={form.billing_model} onValueChange={(v) => setForm({ ...form, billing_model: v })}>
                          <SelectTrigger data-testid="client-billing-model-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="retainer">Retainer (recurring monthly)</SelectItem>
                            <SelectItem value="project_fee">Project fee (one-time)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>{form.billing_model === "retainer" ? "Monthly retainer ($)" : "Project fee ($)"}</Label>
                        <Input
                          type="number" min="0" step="100"
                          value={form.billing_amount}
                          onChange={e => setForm({ ...form, billing_amount: parseFloat(e.target.value) || 0 })}
                          placeholder="0"
                          data-testid="client-billing-amount-input"
                        />
                      </div>
                    </div>
                    {form.billing_model === "project_fee" && (
                      <div className="mt-2">
                        <Label>Fee start date</Label>
                        <Input
                          type="date"
                          value={form.billing_start_date || ""}
                          onChange={e => setForm({ ...form, billing_start_date: e.target.value })}
                          data-testid="client-billing-start-input"
                        />
                        <div className="text-[10px] text-neutral-500 mt-1">Counts as revenue in the month of this date. Leave blank to use today.</div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Status</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger data-testid="client-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lead">Lead</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="client-notes-input" /></div>
                </div>
                <DialogFooter>
                  <Button onClick={create} disabled={!form.company && !form.name} className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="client-create-submit">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <Section>
        {clients.length === 0 ? (
          <div className="border border-dashed border-neutral-300 p-16 grid place-items-center text-center" data-testid="clients-empty">
            <Users size={32} className="text-neutral-400 mb-3" />
            <div className="font-display text-xl font-bold mb-1">{isAdmin ? "No clients yet" : "No accessible clients"}</div>
            <div className="text-sm text-neutral-500 mb-4">
              {isAdmin ? "Add your first client to start tracking work and billing." : "You'll see clients here once you're assigned to a task."}
            </div>
            {isAdmin && (
              <Button onClick={() => setOpen(true)} className="rounded-sm bg-neutral-900 hover:bg-neutral-800">
                <Plus size={14} className="mr-1" /> Add client
              </Button>
            )}
          </div>
        ) : (
          <div className="border border-neutral-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50/50 text-[10px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left px-4 py-3 font-semibold">Company</th>
                  <th className="text-left px-4 py-3 font-semibold">Contact</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-left px-4 py-3 font-semibold">Industry</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  {isSuperAdmin && <th className="text-left px-4 py-3 font-semibold">Billing</th>}
                </tr>
              </thead>
              <tbody>
                {clients.map(c => {
                  const stripe = {
                    active: "border-l-emerald-500",
                    lead: "border-l-sky-500",
                    archived: "border-l-neutral-300",
                  }[c.status] || "border-l-neutral-200";
                  const pill = {
                    active: "bg-emerald-100 text-emerald-800 border-emerald-200",
                    lead: "bg-sky-100 text-sky-800 border-sky-200",
                    archived: "bg-neutral-100 text-neutral-500 border-neutral-200",
                  }[c.status] || "bg-neutral-50 text-neutral-700 border-neutral-200";
                  const dim = c.status === "archived" ? "opacity-60" : "";
                  const isEditing = editingBilling === c.id;
                  return (
                    <tr
                      key={c.id}
                      className={`border-t border-neutral-100 hover:bg-neutral-50 transition-colors border-l-4 ${stripe} ${dim}`}
                      data-testid={`client-row-${c.id}`}
                    >
                      <td className="px-4 py-3 font-medium">
                        <Link to={`/clients/${c.id}`} className="hover:underline">{c.company || c.name}</Link>
                      </td>
                      <td className="px-4 py-3 text-neutral-600">{c.company ? (c.name || "—") : "—"}</td>
                      <td className="px-4 py-3 text-neutral-600 font-mono text-xs">{c.email || "—"}</td>
                      <td className="px-4 py-3 text-neutral-600">{c.industry || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm font-semibold ${pill}`}>
                          {c.status}
                        </span>
                      </td>
                      {isSuperAdmin && (
                        <td className="px-4 py-3" data-testid={`client-billing-cell-${c.id}`}>
                          {isEditing ? (
                            <div className="flex items-center gap-1">
                              <Select value={billingDraft.billing_model} onValueChange={(v) => setBillingDraft({ ...billingDraft, billing_model: v })}>
                                <SelectTrigger className="h-7 w-32 rounded-sm text-xs" data-testid={`client-billing-model-${c.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="retainer">Retainer/mo</SelectItem>
                                  <SelectItem value="project_fee">Project fee</SelectItem>
                                </SelectContent>
                              </Select>
                              <Input
                                type="number" min="0" step="100"
                                value={billingDraft.billing_amount}
                                onChange={e => setBillingDraft({ ...billingDraft, billing_amount: e.target.value })}
                                className="h-7 w-24 rounded-sm font-mono text-xs"
                                data-testid={`client-billing-amount-${c.id}`}
                              />
                              <Button size="sm" onClick={() => saveBilling(c)} className="h-7 w-7 p-0 rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid={`client-billing-save-${c.id}`}><Check size={12} /></Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingBilling(null)} className="h-7 w-7 p-0 rounded-sm" data-testid={`client-billing-cancel-${c.id}`}><X size={12} /></Button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startBillingEdit(c)}
                              className="font-mono text-xs text-neutral-700 hover:text-neutral-900 inline-flex items-center gap-1.5"
                              data-testid={`client-billing-edit-${c.id}`}
                            >
                              <span>{billingLabel(c)}</span>
                              <Pencil size={10} className="text-neutral-400" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
